/**
 * SessionDetailPage Component
 *
 * Displays detailed information about a specific session execution including:
 * - Session metadata (status, timestamps)
 * - Session timeline with events
 * - Output and error messages
 *
 * Route: /sessions/:session_id
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '../hooks/useSessions';
import { SessionTimeline } from '../components/jobs/SessionTimeline';
import { useSessionSSE } from '../contexts/SSEContext';

export const SessionDetailPage: React.FC = () => {
  const { session_id } = useParams<{ session_id: string }>();
  const navigate = useNavigate();

  // Fetch session data
  const { data: session, isLoading, error, refetch } = useSession(session_id);

  // Enable SSE only for running sessions (safe now - stream endpoint won't execute pending sessions)
  const { events: sseEvents, status: sseStatus, error: sseError } = useSessionSSE(
    session_id,
    session?.status === 'running'
  );

  // Auto-refresh when SSE connection closes (stream completes)
  const prevStatusRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    // Detect transition from 'open' to 'closed' (stream completed)
    if (prevStatusRef.current === 'open' && sseStatus === 'closed') {
      console.log('[SessionDetailPage] SSE stream completed, refreshing session data...');
      // Wait a bit for backend to finish writing to database
      setTimeout(() => {
        refetch();
      }, 1000);
    }
    prevStatusRef.current = sseStatus;
  }, [sseStatus, refetch]);

  // State for browser streaming URL
  const [browserStreamUrl, setBrowserStreamUrl] = React.useState<string | null>(null);

  // State for iframe expanded/collapsed
  const [isIframeExpanded, setIsIframeExpanded] = React.useState(false);

  // Ref for events scroll container
  const eventsScrollRef = React.useRef<HTMLDivElement>(null);

  // Extract streaming_url from SSE events (for real-time during running session)
  React.useEffect(() => {
    // Check for close browser event - hide iframe if browser is closed
    // In new format, check if purpose mentions closing browser
    const closeBrowserEvent = sseEvents.find((event) =>
      event.purpose?.toLowerCase().includes('close') && event.purpose?.toLowerCase().includes('browser')
    );
    if (closeBrowserEvent) {
      console.log('Browser closed, hiding iframe');
      setBrowserStreamUrl(null);
      return;
    }

    // Check for initialize event with streaming_url
    const browserInitEvent = sseEvents.find((event) => event.streaming_url);
    if (browserInitEvent && browserInitEvent.streaming_url) {
      console.log('Setting browser stream URL from SSE event:', browserInitEvent.streaming_url);
      setBrowserStreamUrl(browserInitEvent.streaming_url);
    }
  }, [sseEvents]);

  // Also use streaming_url from session (for when page loads with completed session)
  React.useEffect(() => {
    if (session?.streaming_url && !browserStreamUrl) {
      console.log('Setting browser stream URL from session:', session.streaming_url);
      setBrowserStreamUrl(session.streaming_url);
    }
  }, [session?.streaming_url, browserStreamUrl]);

  // Get final output from either session or SSE events
  const finalOutput = React.useMemo(() => {
    // Check SSE events for final_response first (real-time)
    const finalResponseEvent = sseEvents.find(event => event.type === 'final_response');
    if (finalResponseEvent?.data) {
      return finalResponseEvent.data;
    }
    // Fall back to session.output (from database)
    return session?.output;
  }, [sseEvents, session?.output]);

  // Debug render conditions
  console.log('Render conditions:', {
    browserStreamUrl,
    sessionStatus: session?.status,
    hasFinalOutput: !!finalOutput,
    shouldShowIframe: !finalOutput && !!browserStreamUrl,
    shouldShowOutput: !!finalOutput
  });

  // Combine static session_log with real-time SSE events (avoid duplicates)
  const allEvents = React.useMemo(() => {
    const staticEvents = session?.session_log || [];

    // For completed/failed sessions: ONLY use static events from DB
    // SSE events are stale and will cause duplicates
    if (session?.status === 'completed' || session?.status === 'failed') {
      console.log(`[SessionDetailPage] Completed/Failed - Using static events only:`, staticEvents.length);
      return staticEvents;
    }

    // For running sessions: use SSE events if available, otherwise static
    if (session?.status === 'running') {
      if (sseEvents.length > 0) {
        console.log(`[SessionDetailPage] Running - Using SSE events:`, sseEvents.length);
        return sseEvents;
      } else {
        console.log(`[SessionDetailPage] Running - Using static events (no SSE yet):`, staticEvents.length);
        return staticEvents;
      }
    }

    // Pending or other: show static events
    console.log(`[SessionDetailPage] Fallback - Using static events:`, staticEvents.length);
    return staticEvents;
  }, [session?.session_log, session?.status, sseEvents]);

  // Auto-scroll events to bottom when new events arrive
  React.useEffect(() => {
    if (eventsScrollRef.current && allEvents.length > 0) {
      eventsScrollRef.current.scrollTop = eventsScrollRef.current.scrollHeight;
    }
  }, [allEvents.length]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <svg
          className="animate-spin h-8 w-8 text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading session
                </h3>
                <p className="mt-1 text-sm text-red-700">{error.message}</p>
                <button
                  onClick={() => navigate(-1)}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Go back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/jobs/${session.job_id}`)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Back to job"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900">Session Details</h1>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                  session.status === 'completed' ? 'bg-green-100 text-green-800' :
                  session.status === 'running' ? 'bg-blue-100 text-blue-800' :
                  session.status === 'failed' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {session.status}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                ID: {session.session_id}
              </p>
            </div>
            {/* Replay button — navigate to interactive checkpoint replay */}
            {(session.status === 'completed' || session.status === 'failed') && session.streaming_url && (
              <button
                onClick={() => navigate(`/checkpoint/${session.session_id}`)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Replay
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="h-[calc(100vh-120px)]">
        <div className="h-full flex gap-4 px-4 py-4">
          {/* Left Side: Browser Stream or Final Output */}
          <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col ${isIframeExpanded ? 'flex-1' : 'flex-1'}`}>
            {/* Final Output (when final response received) - Show first if available */}
            {finalOutput && (
              <>
                <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex-shrink-0">
                  <h2 className="text-sm font-semibold text-gray-900">Final Output</h2>
                </div>
                <div className="flex-1 overflow-auto p-6">
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <pre className="text-xs whitespace-pre-wrap">
                      <code>{JSON.stringify(finalOutput, null, 2)}</code>
                    </pre>
                  </div>
                </div>
              </>
            )}

            {/* Browser Streaming (when browser initialized but no final output yet) */}
            {!finalOutput && browserStreamUrl && (
              <>
                <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
                  <h2 className="text-sm font-semibold text-gray-900">Live Browser View</h2>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setIsIframeExpanded(!isIframeExpanded)}
                      className="text-gray-600 hover:text-gray-900 p-1"
                      title={isIframeExpanded ? 'Collapse' : 'Expand full width'}
                      aria-label={isIframeExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isIframeExpanded ? (
                        // Collapse icon
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                        </svg>
                      ) : (
                        // Expand icon
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                      )}
                    </button>
                    <a
                      href={browserStreamUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Open in new tab
                    </a>
                  </div>
                </div>
                <div className="flex-1 relative">
                  <iframe
                    src={browserStreamUrl}
                    className="absolute inset-0 w-full h-full border-none"
                    title="Browser Stream"
                    allow="fullscreen"
                  />
                </div>
              </>
            )}

            {/* Error message (when failed with no output or browser stream) */}
            {!finalOutput && !browserStreamUrl && session?.status === 'failed' && session?.error_message && (
              <>
                <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex-shrink-0">
                  <h2 className="text-sm font-semibold text-gray-900">Session Failed</h2>
                </div>
                <div className="flex-1 overflow-auto p-6">
                  <div className="bg-red-50 p-4 rounded border border-red-200">
                    <p className="text-sm text-red-800">{session.error_message}</p>
                  </div>
                </div>
              </>
            )}

            {/* Pending state (when not started) */}
            {!finalOutput && !browserStreamUrl && session?.status === 'pending' && (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <p>Session pending execution...</p>
              </div>
            )}
          </div>

          {/* Right Side: Events Timeline (420px fixed width, hidden when iframe expanded) */}
          {!isIframeExpanded && (
            <div className="w-[420px] bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col flex-shrink-0">
            <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xs font-semibold text-gray-900">
                Events {session?.status === 'running' && <span className="text-blue-600">(Live)</span>}
              </h2>
              {session?.status === 'running' && sseStatus === 'open' && (
                <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></div>
              )}
            </div>

            {/* SSE Error Indicator */}
            {session?.status === 'running' && sseStatus === 'error' && sseError && (
              <div className="bg-red-50 border-b border-red-200 px-3 py-2 flex-shrink-0">
                <p className="text-xs text-red-800">{sseError}</p>
              </div>
            )}

            {/* Events Timeline */}
            <div className="flex-1 overflow-y-auto p-3" ref={eventsScrollRef}>
              {allEvents.length > 0 ? (
                <SessionTimeline events={allEvents} sessionStatus={session?.status} />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-xs">
                    {session?.status === 'running' ? 'Initializing agent...' : 'No events yet'}
                  </p>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  );
};
